/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  APIResponseInterface,
  AvailableRevenuePayout,
  AvailableTransactions,
  FilterTransactionInterFace,
  RequestTransactionInterface,
  TransactionInitialProperties,
} from '../../../interface/partner-interfaces/transaction-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import transaction from '../../../utils/api/partner/transaction';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

// import { fetchListFilters } from './ApprovedStationManagementSlice';

// a thunk to fetch users
export const fetchAllTransactions = createAsyncThunk<
  APIResponseInterface,
  RequestTransactionInterface
>(
  'transaction-management/fetchAllTransactions',
  async (data: RequestTransactionInterface, { dispatch }) => {
    try {
      const response = await transaction.fetchUsers(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((eachTransaction: AvailableTransactions) => ({
          ...eachTransaction,
          date: ConvertUTCtoDate(eachTransaction.createdAt),
          time: FormatUtcString(ConvertUTCtoHHMM(eachTransaction.createdAt)),
          id: eachTransaction._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRevenuePayout = createAsyncThunk<AvailableRevenuePayout[], QueryParameters>(
  'transaction-management/fetchRevenuePayout',
  async (query, { dispatch }) => {
    try {
      const response = await transaction.fetchRevenuePayout(query); // must return an array of AvailableRevenuePayout
      return response.data.results; // assume response.data.results is AvailableRevenuePayout[]
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'transaction-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await transaction.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: TransactionInitialProperties = {
  revenuePayout: [],
  transactions: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 0,
  totalResults: 0,
  filter: {
    fromDate: '',
    toDate: '',
  },
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const transactionSlice = createSlice({
  name: 'transaction-management',
  initialState,
  reducers: {
    /**
     * Set User filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setTransactionFilterData: (
      state: TransactionInitialProperties,
      action: PayloadAction<FilterTransactionInterFace>,
    ) => {
      state.filter = action.payload;
    },

    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetTransactionToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update station management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateTransactionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers functionsd q
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<TransactionInitialProperties>) => {
    builder
      .addCase(fetchRevenuePayout.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchRevenuePayout.fulfilled,
        (state, action: PayloadAction<AvailableRevenuePayout[]>) => {
          state.loading = false;
          state.revenuePayout = action.payload;
        },
      )

      .addCase(fetchRevenuePayout.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchAllTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTransactions.fulfilled,
        (state, action: PayloadAction<APIResponseInterface>) => {
          state.loading = false;
          state.transactions = action.payload.results;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllTransactions.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setTransactionFilterData,
  resetTransactionToInitialState,
  updateTransactionFilters,
  setSortData,
} = transactionSlice.actions;

// Export the reducer
export default transactionSlice.reducer;
